import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AppLockContext = createContext(null);
export const useAppLock = () => useContext(AppLockContext);

export function AppLockProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // null = loading/unknown
  const [locked, setLocked] = useState(false);
  const hiddenAt = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return null;
    try {
      const s = await api.appLockStatus();
      setStatus(s);
      return s;
    } catch {
      setStatus({ enabled: false });
      return null;
    }
  }, [user]);

  // On login / cold start: lock immediately if a passcode is set.
  useEffect(() => {
    if (!user) {
      setStatus(null);
      setLocked(false);
      return;
    }
    let active = true;
    api
      .appLockStatus()
      .then((s) => {
        if (!active) return;
        setStatus(s);
        setLocked(!!s.enabled);
      })
      .catch(() => {
        if (active) {
          setStatus({ enabled: false });
          setLocked(false);
        }
      });
    return () => {
      active = false;
    };
  }, [user]);

  // Re-lock when the app returns from the background after the grace period.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current = Date.now();
        return;
      }
      if (!status?.enabled || locked) return;
      const m = status.relock_minutes;
      if (m === -1) return; // only lock on cold start
      if (hiddenAt.current == null) return;
      if (Date.now() - hiddenAt.current >= (m || 0) * 60000) setLocked(true);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [status, locked]);

  const unlock = useCallback(() => setLocked(false), []);

  return (
    <AppLockContext.Provider value={{ status, locked, setLocked, unlock, refresh }}>
      {children}
    </AppLockContext.Provider>
  );
}
